import "react-app-polyfill/ie11"; // For IE 11 support
import "react-app-polyfill/stable";
import "core-js";
import "./polyfill";
import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import * as serviceWorker from "./serviceWorker";
import reportWebVitals from "./reportWebVitals";
import { Provider } from "react-redux";
import store from "./store";
import { currentEnv, isMaintenanceTime } from "./config/config";
import Outage from "./revampPages/Outage";
import * as SentryReact from "@sentry/react";
import { SequenceNumberProvider } from "./contexts/SequenceNumberContext";

if (currentEnv === "staging" || currentEnv === "prod") {
    SentryReact.init({
        dsn: "https://e486677cbc950ba4404332783c5dbe49@o4508326426181632.ingest.de.sentry.io/4508496551411792",
        integrations: [
            SentryReact.browserTracingIntegration(),
            SentryReact.replayIntegration(),
        ],
        tracesSampleRate: currentEnv === "prod" ? 1.0 : 0.05,
        replaysSessionSampleRate: currentEnv === "prod" ? 0.01 : 0.1,
        replaysOnErrorSampleRate: currentEnv === "prod" ? 0.1 : 1.0,
        environment: currentEnv,
        ignoreErrors: [
            /chrome-extension:/,
            /ExtensionsBundleLoader/,
            /extensions::/,
            /Network Error/,
            /ETIMEDOUT/,
            /ECONNABORTED/,
            / AxiosError /,
            /fetch failed/,
            /node_modules/,
        ],
        beforeSend(event) {
            if (window.location.hostname === "localhost") return null;
            if (isNodeModulesError(event)) return null;

            const reqUrl = event.request?.url || event.transaction || "";
            if (typeof reqUrl === "string" && reqUrl.includes("/monitoring")) return null;

            try {
                const message = event.message || "";
                const frames = event?.exception?.values?.[0]?.stacktrace?.frames || [];

                if (message.includes("BufferLoader") || message.includes("tawk")) {
                    return null;
                }

                for (const f of frames) {
                    const file = f.filename || "";
                    if (file.includes("tawk") || file.includes("twk-") || file.includes("twk_")) {
                        return null;
                    }
                }
            } catch (e) { }

            return event;
        }
    });
}

function isNodeModulesError(event) {
    if (!event.exception?.values) return false;

    for (const exception of event.exception.values) {
        const frames = exception.stacktrace?.frames || [];
        const hasNodeModules = frames.some((frame) => {
            const filename = frame.filename || "";
            const absPath = frame.abs_path || "";
            return (
                filename.includes("node_modules") ||
                absPath.includes("node_modules") ||
                /webpack:\/\/\/\.\/node_modules\//.test(absPath) ||
                absPath.includes("webpack:///./node_modules/") ||
                absPath.includes("~")
            );
        });
        if (hasNodeModules) return true;
    }

    return false;
}

const container = document.getElementById("root");
const root = createRoot(container);

root.render(
    <Provider store={store}>
        <SentryReact.ErrorBoundary>
            <SequenceNumberProvider>
                {isMaintenanceTime ? <Outage /> : <App />}
            </SequenceNumberProvider>
        </SentryReact.ErrorBoundary>
    </Provider>
);

serviceWorker.unregister();
reportWebVitals();
